import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoginFormData, LoginPageState, useLogin } from "../index";

/**
 * LoginForm - 로그인 폼 컴포넌트
 * 상태 관리와 폼 로직 포함
 */
export const LoginForm = () => {
  const navigate = useNavigate();
  const loginMutation = useLogin();

  const [formData, setFormData] = useState<LoginFormData>({
    user_id: "",
    password: "",
    rememberMe: false,
  });

  const [state, setState] = useState<LoginPageState>({
    isLoading: false,
    error: "",
  });

  const handleUserIdChange = (user_id: string) => {
    setFormData((prev) => ({ ...prev, user_id }));
  };

  const handlePasswordChange = (password: string) => {
    setFormData((prev) => ({ ...prev, password }));
  };

  const handleRememberMeChange = (rememberMe: boolean) => {
    setFormData((prev) => ({ ...prev, rememberMe }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setState((prev) => ({ ...prev, error: "" }));
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      // React Query mutation 실행
      await loginMutation.mutateAsync({
        user_id: formData.user_id,
        password: formData.password,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "로그인 실패. 다시 시도해주세요.";
      setState((prev) => ({
        ...prev,
        error: errorMessage,
      }));
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  return (
    <div className="w-full bg-white rounded-2xl shadow-2xl p-8 md:p-16">
      {/* GGP 로고 */}
      <div className="flex justify-center mb-8 md:mb-12">
        <div className="text-center">
          <h2 className="text-6xl md:text-9xl font-bold leading-none">
            <span className="text-orange-600">G</span>
            <span className="text-yellow-400">G</span>
            <span className="text-green-600">P</span>
          </h2>
          <p className="text-xs text-gray-400 mt-2 md:mt-3 tracking-widest">
            GOOD GROWTH PLANNER
          </p>
        </div>
      </div>

      {/* 폼 */}
      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
        {/* 에러 메시지 */}
        {state.error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {state.error}
          </div>
        )}

        {/* 아이디 입력 */}
        <div className="space-y-2 md:space-y-3">
          <label className="block text-sm md:text-base font-medium text-gray-700">
            아이디
          </label>
          <input
            type="text"
            value={formData.user_id}
            onChange={(e) => handleUserIdChange(e.target.value)}
            placeholder="아이디를 입력해주세요"
            className="w-full px-4 md:px-5 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition"
          />
        </div>

        {/* 비밀번호 입력 */}
        <div className="space-y-2 md:space-y-3">
          <label className="block text-sm md:text-base font-medium text-gray-700">
            비밀번호
          </label>
          <input
            type="password"
            value={formData.password}
            onChange={(e) => handlePasswordChange(e.target.value)}
            placeholder="비밀번호를 입력해주세요"
            className="w-full px-4 md:px-5 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition"
          />
        </div>

        {/* 로그인 옵션 */}
        <div className="flex items-center justify-between">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.rememberMe}
              onChange={(e) => handleRememberMeChange(e.target.checked)}
              className="w-4 h-4 border-gray-300 rounded cursor-pointer accent-orange-500"
            />
            <span className="text-sm md:text-base text-gray-600">
              로그인 유지
            </span>
          </label>
        </div>

        {/* 로그인 버튼 */}
        <button
          type="submit"
          disabled={state.isLoading}
          className="w-full py-3 md:py-4 text-base md:text-lg bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
        >
          {state.isLoading ? "로그인 중..." : "로그인"}
        </button>
      </form>

      {/* 링크들 */}
      <div className="flex items-center justify-center gap-4 md:gap-6 mt-6 md:mt-8 text-xs md:text-sm text-gray-600">
        <button
          type="button"
          onClick={() => navigate("/account-recovery?mode=id")}
          className="hover:text-orange-500 transition bg-none border-none p-0 cursor-pointer"
        >
          아이디 찾기
        </button>
        <span className="text-gray-300">|</span>
        <button
          type="button"
          onClick={() => navigate("/account-recovery?mode=password")}
          className="hover:text-orange-500 transition bg-none border-none p-0 cursor-pointer"
        >
          비밀번호 찾기
        </button>
        <span className="text-gray-300">|</span>
        <button
          type="button"
          onClick={() => navigate("/signup")}
          className="hover:text-orange-500 transition bg-none border-none p-0 cursor-pointer"
        >
          회원가입
        </button>
      </div>
    </div>
  );
};
